import { FC } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import { Grid, TextField, Box, ButtonGroup, Button, Divider, InputAdornment } from '@mui/material';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { putFinanceInfo } from '../../../../fetch';
import { IFinanceInfo } from '../../../../models';
import { Loader } from '../../../../components';

interface IFinanceInformation {
  financeInfo: IFinanceInfo;
  isLoadingFinanceInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchFinanceInfo: () => void;
}

const FinanceInfoSchema = Yup.object().shape({
  lotCost: Yup.number()
    .nullable()
    .typeError('Lot Cost must be a number')
    .max(200000, 'Lot Cost must be less than 200,000'),
  lotPremium: Yup.number()
    .nullable()
    .typeError('Lot Premium must be a number')
    .max(200000, 'Lot Premium must be less than 200,000'),
  basisAdder: Yup.number()
    .nullable()
    .typeError('Basis Adder must be a number')
    .max(999999, 'Basis Adder must be less than 999,999'),
  constructionLender: Yup.string().nullable().max(100, 'Text field must not exceed 100 characters'),
  constructionLoanAmount: Yup.number()
    .nullable()
    .typeError('Construction Loan Amount must be a number')
    .max(1000000, 'Construction Loan Amount must be less than 1,000,000'),
  monthlyInterestRate: Yup.number()
    .nullable()
    .typeError('Monthly Interest Rate must be a number')
    .max(100, 'Monthly Interest Rate must be less than 100'),
  monthlyAccrualRate: Yup.number()
    .nullable()
    .typeError('Monthly Accrual Rate must be a number')
    .max(100, 'Monthly Accrual Rate must be less than 100')
});

export const FinanceInfo: FC<IFinanceInformation> = ({
  financeInfo,
  isLoadingFinanceInfo,
  setDirtyForm,
  refetchFinanceInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();

  const formatNumber = value => {
    if (value % 1 === 0) {
      value += '.00';
    }

    return value;
  };

  return (
    <>
      {isLoadingFinanceInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingFinanceInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...financeInfo,
            lotCost: financeInfo?.lotCost ? formatNumber(financeInfo?.lotCost) : '',
            lotPremium: financeInfo?.lotPremium ? formatNumber(financeInfo?.lotPremium) : '',
            basisAdder: financeInfo?.basisAdder ? formatNumber(financeInfo?.basisAdder) : '',
            constructionLender: financeInfo?.constructionLender ?? '',
            constructionLoanAmount: financeInfo?.constructionLoanAmount
              ? formatNumber(financeInfo?.constructionLoanAmount)
              : '',
            monthlyInterestRate: financeInfo?.monthlyInterestRate
              ? formatNumber(financeInfo?.monthlyInterestRate)
              : '',
            monthlyAccrualRate: financeInfo?.monthlyAccrualRate
              ? formatNumber(financeInfo?.monthlyAccrualRate)
              : ''
          }}
          validationSchema={FinanceInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IFinanceInfo = {
                ...values
              };

              await putFinanceInfo(parseInt(jobId), data);
              refetchFinanceInfo();
              enqueueSnackbar('Finance info has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Job was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Lot Cost"
                              name="lotCost"
                              size="small"
                              value={values.lotCost}
                              onBlur={handleBlur}
                              error={touched.lotCost && Boolean(errors.lotCost)}
                              helperText={touched.lotCost && errors.lotCost}
                              onChange={e => setFieldValue('lotCost', e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Lot Premium"
                              name="lotPremium"
                              size="small"
                              value={values.lotPremium}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('lotPremium', e.target.value)}
                              error={touched.lotPremium && Boolean(errors.lotPremium)}
                              helperText={touched.lotPremium && errors.lotPremium}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Basis Adder"
                              name="basisAdder"
                              size="small"
                              value={values.basisAdder}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('basisAdder', e.target.value)}
                              error={Boolean(touched.basisAdder && errors.basisAdder)}
                              helperText={touched.basisAdder && errors.basisAdder}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Construction Lender"
                              name="constructionLender"
                              size="small"
                              value={values.constructionLender}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('constructionLender', e.target.value)}
                              error={
                                touched.constructionLender && Boolean(errors.constructionLender)
                              }
                              helperText={touched.constructionLender && errors.constructionLender}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Construction Loan Amount"
                              name="constructionLoanAmount"
                              size="small"
                              value={values.constructionLoanAmount}
                              onBlur={handleBlur}
                              onChange={e =>
                                setFieldValue('constructionLoanAmount', e.target.value)
                              }
                              error={
                                touched.constructionLoanAmount &&
                                Boolean(errors.constructionLoanAmount)
                              }
                              helperText={
                                touched.constructionLoanAmount && errors.constructionLoanAmount
                              }
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Monthly Interest Rate %"
                              name="monthlyInterestRate"
                              size="small"
                              value={values.monthlyInterestRate}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('monthlyInterestRate', e.target.value)}
                              error={
                                touched.monthlyInterestRate && Boolean(errors.monthlyInterestRate)
                              }
                              helperText={touched.monthlyInterestRate && errors.monthlyInterestRate}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Monthly Accrual Rate %"
                              name="monthlyAccrualRate"
                              size="small"
                              value={values.monthlyAccrualRate}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('monthlyAccrualRate', e.target.value)}
                              error={
                                touched.monthlyAccrualRate && Boolean(errors.monthlyAccrualRate)
                              }
                              helperText={touched.monthlyAccrualRate && errors.monthlyAccrualRate}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
